import { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, Check, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROUNDS = 5;
const CLIP = 12;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Blind test : un extrait sans image, puis le bon titre parmi 3 grands boutons.
export default function BlindTest({ tracks, onComplete }) {
  const rounds = useMemo(() => shuffle(tracks).slice(0, Math.min(ROUNDS, tracks.length)).map((t) => ({
    answer: t, options: shuffle([t, ...shuffle(tracks.filter((x) => x.id !== t.id && x.title !== t.title)).slice(0, 2)]),
  })), [tracks]);
  const [n, setN] = useState(0);
  const [picked, setPicked] = useState(null);
  const [good, setGood] = useState(0);
  const audio = useRef(null);
  const timer = useRef(null);
  const started = useRef(Date.now());
  const round = rounds[n];

  const play = (full = false) => {
    const a = audio.current;
    if (!a) return;
    clearTimeout(timer.current);
    a.currentTime = round.answer.start || 0;
    a.play().catch(() => {});
    if (!full) timer.current = setTimeout(() => a.pause(), CLIP * 1000);
  };
  useEffect(() => { setPicked(null); const id = setTimeout(() => play(), 400); return () => clearTimeout(id); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { clearTimeout(timer.current); audio.current?.pause(); }, []);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt.id === round.answer.id) setGood((g) => g + 1);
    play(true);
  };
  const next = () => {
    audio.current?.pause();
    if (n + 1 < rounds.length) { setN(n + 1); return; }
    const score = Math.round((good / rounds.length) * 100);
    onComplete({ score, accuracy: score, duration: Math.round((Date.now() - started.current) / 1000) });
  };
  if (!round) return null;
  const right = picked && picked.id === round.answer.id;
  return (
    <div className="w-full max-w-2xl soft-in text-center" data-testid="blind-test">
      <p className="font-patient text-xl text-gray-500 mb-2" data-testid="blind-test-progress">Chanson {n + 1} sur {rounds.length}</p>
      <h2 className="font-patient font-black text-3xl text-gray-800 mb-6">Quelle est cette chanson ?</h2>
      <audio ref={audio} src={`${BACKEND}${round.answer.audio_url}`} preload="auto" />
      <button onClick={() => play()} data-testid="blind-test-replay"
        className="mx-auto mb-8 w-28 h-28 rounded-full bg-rose-500 hover:bg-rose-600 shadow-lg flex items-center justify-center transition-colors" aria-label="Réécouter l'extrait">
        <Volume2 className="w-14 h-14 text-white" />
      </button>
      <div className="space-y-4">
        {round.options.map((o) => {
          const isAns = o.id === round.answer.id;
          const cls = !picked ? "bg-white border-gray-200 hover:border-rose-300" : isAns ? "bg-emerald-50 border-emerald-400" : picked.id === o.id ? "bg-amber-50 border-amber-300" : "bg-white border-gray-100 opacity-60";
          return (
            <button key={o.id} onClick={() => choose(o)} disabled={!!picked} data-testid={`blind-test-option-${o.id}`}
              className={`w-full rounded-3xl border-4 px-6 py-5 font-patient font-black text-2xl text-gray-800 flex items-center justify-between transition-colors ${cls}`}>
              <span className="text-left">{o.title}</span>
              {picked && isAns && <Check className="w-8 h-8 text-emerald-600 shrink-0" />}
              {picked && !isAns && picked.id === o.id && <X className="w-8 h-8 text-amber-600 shrink-0" />}
            </button>
          );
        })}
      </div>
      {picked && (
        <div className="mt-8" data-testid="blind-test-feedback">
          <p className="font-patient text-2xl text-gray-700 mb-4">
            {right ? "Bravo, c'est bien elle !" : "C'était :"} <strong>{round.answer.title}</strong>{round.answer.artist && ` · ${round.answer.artist}`}
          </p>
          <button onClick={next} data-testid="blind-test-next"
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-patient font-black text-2xl px-10 h-16 transition-colors">
            {n + 1 < rounds.length ? "Chanson suivante" : "Terminer"}
          </button>
        </div>
      )}
    </div>
  );
}
